import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { SectionHeading } from 'site-modules/shared/components/editorial/section-heading/section-heading';
import { Link } from 'site-modules/shared/components/link/link';
import './category-cards.scss';

export function CategoryCard({
  content,
  isLabelHidden = false,
  hideMobilePromoText = true,
  enableClickTracking = false,
  noFollowLinks = false,
  contentClass = '',
  cardHeaderTag = null,
  isMobile = false,
}) {
  const image = content.metadata('item-image').value();
  const promoText = content.metadata('item-text').value();
  const title = content.metadata('item-title').value();
  const url = content.metadata('item-link').value();
  const promoLabel = content.metadata('promoLabel').value();
  const sponsoredTitle = content.metadata('sponsored-title').value();

  const imagePath = getFullImageUrl(image, IMAGE_SIZES.sm_md);

  const cardHeaderClassName = 'size-20 mb-1';
  const CardHeader = cardHeaderTag;

  return (
    <div className="category-card pt-0_5 mb-1 pos-r">
      <Link
        to={url}
        data-tracking-id={enableClickTracking ? 'home_content_feature_article_click' : null}
        data-tracking-value={sponsoredTitle ? `SPONSORED:${title}` : `UNSPONSORED:${title}`}
        rel={noFollowLinks ? 'nofollow' : null}
        className="category-card-link fw-bold text-cool-gray-10"
      >
        {!isLabelHidden && (
          <div className={classnames('promo-label mb-0_5', { 'hidden-md-down': hideMobilePromoText })}>
            {/* todo - review PLAT-3859 disabling */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            {promoLabel && <a className="h7 text-primary fw-bold text-uppercase">{promoLabel}</a>}
          </div>
        )}
        <div
          className="hover-wrapper overflow-hidden pos-r mb-1_25"
          style={{ aspectRatio: isMobile ? '3 / 2' : '16 /  9' }}
        >
          <img src={imagePath} alt="" className="category-card-image w-100 h-100" loading="lazy" decoding="async" />
          {sponsoredTitle && (
            <div
              className="text-white py-0_25 px-1 size-12 font-weight-500 pos-a d-inline-block bg-blue-50 rounded"
              style={{
                border: '1px solid #1358BF',
                bottom: '1rem',
                left: '1rem',
                top: 'initial',
                width: 'auto',
                transform: 'none',
              }}
            >
              Sponsored Content
            </div>
          )}
        </div>
        <div className="mt-0_75 px-md-1">
          {cardHeaderTag ? <CardHeader className={cardHeaderClassName}>{title}</CardHeader> : title}
          <div
            className={classnames('text-cool-gray-30 fw-normal', contentClass, {
              'hidden-md-down': hideMobilePromoText,
            })}
          >
            {promoText}
          </div>
        </div>
      </Link>
    </div>
  );
}

CategoryCard.propTypes = {
  content: PropTypes.shape({}).isRequired,
  isLabelHidden: PropTypes.bool,
  enableClickTracking: PropTypes.bool,
  enableEdmundsTitle: PropTypes.bool,
  hideMobilePromoText: PropTypes.bool,
  noFollowLinks: PropTypes.bool,
  contentClass: PropTypes.string,
  cardHeaderTag: PropTypes.string,
  isMobile: PropTypes.bool,
};

export function CategoryCards({
  entry,
  enableEdmundsTitle,
  enableClickTracking = false,
  hideMobilePromoText = true,
  sectionHeadingClassName = null,
  noFollowLinks = false,
  className = null,
  headerTag = 'header',
  cardHeaderTag = null,
  isMobile = false,
}) {
  const { title, linkout } = (() => {
    const data = entry.getAllMetadata();
    return {
      title: data.title,
      linkout: {
        href: data['view-more-url'],
        text: data['view-more-title'],
      },
    };
  })();

  return (
    <Fragment>
      {title && (
        <SectionHeading linkout={linkout} linkoutText={linkout.text} {...sectionHeadingClassName} tag={headerTag}>
          {title}
        </SectionHeading>
      )}
      <Row className={classnames('category-cards', className)}>
        {entry.children().map(category => {
          return (
            <Col xs={12} md={6} lg={4} key={category.metadata('item-title').value()} className="px-lg-0_75 px-xl-1_25">
              <CategoryCard
                content={category}
                enableClickTracking={enableClickTracking}
                hideMobilePromoText={hideMobilePromoText}
                enableEdmundsTitle={enableEdmundsTitle}
                noFollowLinks={noFollowLinks}
                cardHeaderTag={cardHeaderTag}
                isMobile={isMobile}
              />
            </Col>
          );
        })}
      </Row>
    </Fragment>
  );
}

CategoryCards.propTypes = {
  entry: PropTypes.shape({}).isRequired,
  enableClickTracking: PropTypes.bool,
  hideMobilePromoText: PropTypes.bool,
  noFollowLinks: PropTypes.bool,
  sectionHeadingClassName: PropTypes.shape({
    textClassName: PropTypes.string,
    containerClassName: PropTypes.string,
  }),
  className: PropTypes.string,
  headerTag: PropTypes.string,
  cardHeaderTag: PropTypes.string,
  isMobile: PropTypes.bool,
};
